import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PORTAL, usePortal } from '../portalAuth';
import { STORE_BASE } from '../routeBase';

const STATUS_LABEL = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', rejected: 'Rejected' };
const STATUS_COLOR = { open: '#991b1b', in_progress: '#92400e', resolved: '#166534', closed: '#475569', rejected: '#7f1d1d' };
const TYPE_LABEL = { warranty: 'Warranty', breakage: 'Breakage', wrong_item: 'Wrong Item', missing_item: 'Missing Item', other: 'Other' };

// VIEW + REPLY only — a customer can add messages to the conversation but
// can't change type/priority/status themselves, same "staff decides the
// classification, customer just talks to them" boundary as the order pages.
export default function SupportTicketDetail() {
  const { id } = useParams();
  const { customer, ready } = usePortal();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);

  const load = () => {
    PORTAL.get(`/support-tickets/${id}`)
      .then(r => setTicket(r.data.ticket))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (customer) load(); }, [customer, id]); // eslint-disable-line

  const postReply = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      await PORTAL.post(`/support-tickets/${id}/comments`, { message: reply.trim() });
      setReply('');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not send reply'); }
    setPosting(false);
  };

  if (!ready || loading) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Please sign in</h2>
      <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)' }}>Sign in</Link>
    </div>
  );
  if (notFound || !ticket) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Ticket not found</h2>
      <Link to={`${STORE_BASE}/support-tickets`} style={{ color: 'var(--sf-accent)' }}>← Back to Support Tickets</Link>
    </div>
  );

  const canReply = !['closed', 'rejected'].includes(ticket.status);

  return (
    <div style={{ maxWidth: 720, margin: '24px auto', padding: '0 24px' }}>
      <Link to={`${STORE_BASE}/support-tickets`} style={{ fontSize: 13, color: '#888', textDecoration: 'none' }}>← Back to Support Tickets</Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '10px 0 18px', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{ticket.subject}</h1>
          <div style={{ fontSize: 12.5, color: '#888', marginTop: 4 }}>
            {ticket.ticket_number} · {TYPE_LABEL[ticket.type]}{ticket.order_number ? ` · Order ${ticket.order_number}` : ''} · Opened {new Date(ticket.created_at).toLocaleDateString()}
          </div>
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 12px', borderRadius: 14, background: `${STATUS_COLOR[ticket.status]}18`, color: STATUS_COLOR[ticket.status] }}>
          {STATUS_LABEL[ticket.status]}
        </span>
      </div>

      {ticket.description && (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 18 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>Original Description</div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', color: '#333' }}>{ticket.description}</div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 10 }}>Conversation</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {(ticket.comments || []).length === 0 && <div style={{ fontSize: 13, color: '#aaa', fontStyle: 'italic' }}>No messages yet — our team will respond here once they review your ticket.</div>}
          {(ticket.comments || []).map(c => (
            <div key={c.id} style={{
              alignSelf: c.author_type === 'customer' ? 'flex-end' : 'flex-start',
              maxWidth: '85%', background: c.author_type === 'customer' ? 'var(--sf-accent, #85cde9)' : '#f1f5f9',
              color: c.author_type === 'customer' ? '#191919' : '#333',
              borderRadius: 10, padding: '9px 13px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, marginBottom: 2 }}>
                {c.author_type === 'customer' ? 'You' : (c.author_name || 'Support Team')} · {new Date(c.created_at).toLocaleString()}
              </div>
              <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{c.message}</div>
            </div>
          ))}
        </div>

        {canReply ? (
          <div>
            <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Write a reply…"
              style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={postReply} disabled={posting || !reply.trim()} className="sf-btn-outline"
                style={{ padding: '9px 20px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                {posting ? 'Sending…' : 'Send Reply'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: '#999', fontStyle: 'italic' }}>This ticket is {STATUS_LABEL[ticket.status].toLowerCase()} and no longer accepts replies.</div>
        )}
      </div>
    </div>
  );
}
